<script setup lang="ts">
import RewardItem from './RewardItem.vue'

import type { LoyaltyReward } from '@/types/types'
import RewardModalMarketingFlow from '@/views/autopilot/Components/RewardModalMarketingFlow.vue'
import { useVModel } from '@vueuse/core'

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    selectedRewardId?: number | null
    rewards: LoyaltyReward[]
    label?: string
    required?: boolean
    buttonShown?: ('edit' | 'clear' | 'change')[]
    showValidityInDays?: boolean
    validityInDays?: number
    showPoints?: boolean
    hiddenRewardIds?: number[]
    noBorderFrame?: boolean
    addButtonClass?: string
    rewardItemClass?: string
    useExternalChangeHandler?: boolean
    selectionUsage?: 'marketing_flow' | 'offer'
  }>(),
  {
    showPoints: false,
    useExternalChangeHandler: false,
    selectionUsage: 'marketing_flow'
  }
)

const validityInDays = useVModel(props, 'validityInDays')
const selectedRewardId = useVModel(props, 'selectedRewardId')

const emit = defineEmits<{
  (e: 'update:selectedRewardId', value: number | null): void
  (e: 'selectReward', value: number): void
  (e: 'clearReward'): void
  (e: 'changeReward', rewardId: number | null): void
  (e: 'editReward', rewardId: number): void
}>()

const isRewardModalOpen = ref<boolean>(false)
const isCreateEditRewardModalOpen = ref<boolean>(false)

const selectedReward = computed(() => props.rewards.find((reward) => reward.id === selectedRewardId.value))

const handleSelectReward = (rewardId: number) => {
  selectedRewardId.value = rewardId
  emit('selectReward', rewardId)
  isRewardModalOpen.value = false
  isCreateEditRewardModalOpen.value = false
}

const openRewardModal = () => {
  if (props.useExternalChangeHandler) {
    emit('changeReward', null)
    return
  }
  isCreateEditRewardModalOpen.value = false
  isRewardModalOpen.value = true
}

const handleEditReward = (rewardId: number) => {
  selectedRewardId.value = rewardId
  if (props.useExternalChangeHandler) {
    emit('editReward', rewardId)
    return
  }
  isRewardModalOpen.value = false
  isCreateEditRewardModalOpen.value = true
}

const handleChangeReward = () => {
  if (props.useExternalChangeHandler) {
    emit('changeReward', selectedRewardId.value ?? null)
    return
  }
  isRewardModalOpen.value = true
}

const handleClearReward = () => {
  selectedRewardId.value = null
  emit('clearReward')
}
</script>

<template>
  <div class="flex flex-col justify-stretch">
    <label v-if="label" class="mb-1.5 text-sm text-foreground">
      {{ label }}
      <span v-if="required" class="text-red-500">*</span>
    </label>

    <div
      v-if="!selectedRewardId"
      :class="[
        'flex w-full flex-row items-center justify-center',
        { 'rounded-md border bg-white p-4': !noBorderFrame }
      ]"
    >
      <Button variant="outline" :class="['mx-3 pl-3', addButtonClass]" @click="openRewardModal">
        <BaseIcon name="HeroPlusIconSolid" class="h-6 w-6 text-foreground" />
        <span class="text-foreground">
          {{ t('pages.marketingFlow.marketingFlowEditPage.action.addReward') }}
        </span>
      </Button>
    </div>

    <div
      v-else
      :class="[
        'flex w-full flex-row items-center justify-center',
        { 'rounded-md border bg-white p-4': !noBorderFrame }
      ]"
    >
      <RewardItem
        v-if="selectedReward"
        :reward="selectedReward"
        is-reward-selected
        :button-shown="buttonShown || ['edit', 'change']"
        :reward-item-class="rewardItemClass"
        @change-reward="handleChangeReward"
        @edit-reward="handleEditReward"
        @clear-reward="handleClearReward"
      />
    </div>

    <RewardModalMarketingFlow
      v-model:selected-reward-id="selectedRewardId"
      v-model:is-selection-modal-open="isRewardModalOpen"
      v-model:is-create-edit-reward-modal-open="isCreateEditRewardModalOpen"
      v-model:validity-in-days="validityInDays"
      :usage="selectionUsage"
      :show-validity-in-days="showValidityInDays"
      :rewards="rewards.filter((reward) => !hiddenRewardIds?.includes(reward.id))"
      :show-points="showPoints"
      @handle-select-reward="handleSelectReward"
    />
  </div>
</template>
